<template>
  <CategoryTop small :title="$t('global.breadcrumbs.singleMailbox')" :breadcrumbs-parent-routes="['account', 'mailbox']"
    :description="$t('mailbox.desc')" />
  <Loader v-if="detailLoading" :show="detailLoading" />
  <div class="mail-reader position-relative" v-else-if="message">
    <MDBRow class="sender-row">
      <MDBCol col="12" sm="3" xl="2">
        <div class="d-flex align-items-center">
          <p class="msg-label text-uppercase fw-bold mb-0 d-flex align-items-center">
            {{ $t("mailbox.message.sender") }}:
          </p>
        </div>
      </MDBCol>
      <MDBCol col="12" sm="9" xl="10" class="px-0">
        <div class="d-flex justify-content-between align-items-center h-100 mx-0">
          <div class="d-flex align-items-center accent-blue msg-label fw-bold mb-0 px-3">
            {{ senderLabel }}
          </div>

          <div class="d-flex justify-content-end align-items-center buttons-container">
            <button v-if="index !== 0" type="button" class="btn font-18 py-1" @click="openMessage(index - 1)">
              <MDBIcon icon="chevron-left" size="1x" />
            </button>
            <span>{{ index + 1 }} {{ $t(`global.of`).toLowerCase() }} {{ length }}</span>
            <button v-if="index + 1 !== length" type="button" class="btn font-18 pe-1" @click="openMessage(index + 1)">
              <MDBIcon icon="chevron-right" size="1x" />
            </button>
          </div>
        </div>
      </MDBCol>
    </MDBRow>
    <MDBRow class="topic-row">
      <MDBCol col="12" sm="3" xl="2">
        <div class="d-flex align-items-center">
          <p class="msg-label text-uppercase fw-bold mb-0 d-flex align-items-center">
            {{ $t("mailbox.message.thread") }}:
          </p>
        </div>
      </MDBCol>
      <MDBCol col="12" sm="9" xl="10" class="d-flex align-items-center accent-blue">
        <div class="d-flex justify-content-between align-items-center h-100 mx-0 msg-label fw-bold">
          {{ messageTitle }}
        </div>
      </MDBCol>
    </MDBRow>
    <MDBRow>
      <MDBCol sm="8" xl="10">
        <div v-html="messageContent" class="mail-reader-content"></div>
      </MDBCol>
      <MDBCol sm="4" xl="2">
        <div class="mail-reader-date">
          {{ parseDate(message.created_at) }}
        </div>
      </MDBCol>
    </MDBRow>
  </div>
</template>

<script setup>
import { MDBRow, MDBCol, MDBIcon } from "mdb-vue-ui-kit";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { computed, watch } from "vue";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import CategoryTop from "@/components/General/CategoryTop/index.vue";
import Loader from "@/components/General/Loader/index.vue";
import dates from "@/plugins/dates";
import { useI18n } from "vue-i18n";
import { pickTranslation } from "@/utils/pickTranslation";

const { t } = useI18n();

const store = useStore();
const route = useRoute();
const router = useRouter();

const currentLocale = computed(() => store.getters.getCurrentLocale);
const detailLoading = computed(() => store.getters.getMessageDetailLoading);
const messages = computed(() => store.getters.getMessages);
const message = computed(() => store.getters.getMessageDetail);

const messageTitle = computed(() => {
  if (!message.value) {
    return "";
  }

  const translated = pickTranslation(
    message.value.messages?.title,
    currentLocale.value
  );
  if (translated) {
    return translated;
  }

  if (message.value.threads?.slug) {
    return t(`message.threads.${message.value.threads.slug}.title`);
  }

  return "";
});

const messageContent = computed(() => {
  if (!message.value) {
    return "";
  }

  const translated = pickTranslation(
    message.value.messages?.content,
    currentLocale.value
  );
  if (translated) {
    return translated;
  }

  if (message.value.threads?.slug) {
    return t(`message.threads.${message.value.threads.slug}.content`);
  }

  return "";
});

const senderLabel = computed(() => {
  const sender = message.value?.threads?.sender;
  if (sender) {
    return t(`mailbox.sender.${sender}`);
  }
  return t("mailbox.sender.system");
});

const index = computed(() => {
  return messages.value.findIndex((item) => item.id == route.params.id);
});

const length = computed(() => messages.value.length);

const openMessage = (listIndex) => {
  const listItem = messages.value[listIndex];
  if (!listItem) {
    return;
  }
  router.push({ name: "singleMailbox", params: { id: listItem.id } });
};

const parseDate = (date) => {
  const time = calculateTime(date);
  return `${dates.parseDate(date)} (${time})`;
};

const calculateTime = (date) => {
  const messageDate = new Date(date);
  const now = Date.now();

  const seconds = parseInt((now - messageDate) / 1000);
  const minutes = parseInt((now - messageDate) / 60000);
  const hours = parseInt((now - messageDate) / 3600000);
  const days = parseInt((now - messageDate) / (24 * 3600 * 1000));

  if (days > 0) {
    return t("mailbox.time.daysAgo", { time: days });
  }

  if (hours > 0) {
    return t("mailbox.time.hoursAgo", { time: hours });
  }

  if (minutes > 0) {
    return t("mailbox.time.minutesAgo", { time: minutes });
  }

  if (seconds > 0) {
    return t("mailbox.time.secondsAgo", { time: seconds });
  }

  return "";
};

const loadDetail = async (id) => {
  if (!id) {
    return;
  }

  if (!messages.value.length) {
    await store.dispatch("loadMessages");
  }

  try {
    await store.dispatch("loadMessageDetail", id);
  } catch (error) {
    if (error?.response?.status === 404) {
      toast(
        t(
          "toast.messageNotFound",
          "This message no longer exists or is not available."
        ),
        {
          type: "error",
          theme: "colored",
          transition: toast.TRANSITIONS.SLIDE,
          position: toast.POSITION.BOTTOM_RIGHT,
        }
      );
      router.push({ name: "mailbox" });
    }
  }
};

watch(
  () => route.params.id,
  (id) => {
    loadDetail(id);
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.mail-reader {
  background: white;
  padding: 25px;
  min-height: 250px;
}

.mail-reader-date,
.mail-reader-content {
  padding-top: 15px;
}

.mail-reader-content {
  border-top: 1px solid $borderLightGray;
}

.msg-label {
  font-size: 18px;
  height: 100%;
}

.back-link {
  padding-left: 24px;
}

.sender-row {
  height: 47px;
}

.topic-row {
  height: 62px;
}

.accent-blue {
  background: $accentLightBlue;
}
</style>
